import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Icon } from '@.ui';
import { BatteryChargeIcon, ChambersFillnessIcon } from '@/Components/Icons';
import { Layout } from '@/Components/Layouts';
import { LinkHelper } from '@/Services';
import { ApiV2 } from '@/Services/api';
import { NotifiableType } from '@/Services/api/methods/v2/Notifications/request';
import { DeviceChambersState, DeviceMonitoringResource } from '@/Services/api/methods/v2/Notifications/response';
import { notificationsStoreSelectors } from '@/Store/notifications/notificationsState';

import { NotifiableFilters } from '../NotifiableFilters';
import { NotificationsList } from '../NotificationsList';
import S from './styles.module.scss';

export const DeviceNotifications: FC = () => {
    const navigator = useNavigate();
    const { stationId, deviceId } = useParams();

    const readNotifications = notificationsStoreSelectors.use.readNotifications();

    const [device, setDevice] = useState<DeviceMonitoringResource>();

    const handleReadAllClick = () => {
        readNotifications({
            notifiable: {
                type: NotifiableType.DEVICE,
                id: Number(deviceId),
            },
            notifications_ids: [],
        });
    };

    useEffect(() => {
        ApiV2.notifications()
            .getDevice(Number(stationId), Number(deviceId))
            .onNotFound(() => {
                navigator(LinkHelper.iRomaScentsNotifications);
            })
            .onForbidden(() => {
                navigator(LinkHelper.iRomaScentsNotifications);
            })
            .onSuccess((res) => {
                setDevice(res.data.device);
            });
    }, [stationId, deviceId, navigator]);

    return (
        <Layout
            name="device_notifications"
            breadcrumbs={[
                { name: 'Notifications', link: LinkHelper.iRomaScentsNotifications },
                { name: device?.station_name ?? '', link: LinkHelper.iRomaScentsNotificationsStationId(Number(stationId)) },
                { name: device?.identifier?.replace(/:/g, '').slice(-8) ?? '', current: true },
            ]}
            nobg
        >
            <div className={S.header}>
                <div className={S.header_top}>
                    <h1 className={S.header_title}>{device?.station_name}</h1>
                    <div className={S.back} onClick={() => navigator(-1)}>
                        <Icon name="chevron-left" size={24} color="currentColor" />
                        <span>Back</span>
                    </div>
                </div>
                <div className={S.information}>
                    <div className={S.information_item}>
                        <span className={S.information_title}>Device ID</span>
                        <span className={S.information_name}>{device?.identifier?.replace(/:/g, '').slice(-8)}</span>
                    </div>
                    <div className={S.information_item}>
                        <span className={S.information_title}>MAC address</span>
                        <span className={S.information_name}>{device?.mac_address}</span>
                    </div>
                    {device && (
                        <div className={S.information_statuses}>
                            <ChambersFillnessIcon
                                color={device.chambers_fullness.color}
                                className={cn({ [S.flicker]: device.chambers_fullness.state === DeviceChambersState.FLICKERING })}
                            />
                            <div className={S.divider} />
                            <BatteryChargeIcon color={device.battery_level.color} />
                            {device.is_firmware_update_available && (
                                <>
                                    <div className={S.divider} />
                                    <Icon name="warning" size={20} />
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className={S.actions}>
                    <NotifiableFilters />
                    <Button onClick={handleReadAllClick} color="transparent" className={S.read}>
                        Read All
                    </Button>
                </div>
            </div>
            <NotificationsList id={Number(deviceId)} type={NotifiableType.DEVICE} />
        </Layout>
    );
};
